import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { API } from "@/apiConfig";

const AI_STYLES = [
  { value: "simple_icon", label: "Simple Icon", desc: "Flat vector, bold shapes, minimal detail" },
  { value: "glossy_pos", label: "Glossy POS", desc: "Premium 3D badge, shiny highlights" },
  { value: "realistic_product", label: "Realistic Product", desc: "Photorealistic studio product shot" },
  { value: "clipart", label: "Clipart", desc: "Fun bold clipart illustration" },
  { value: "minimal", label: "Minimal", desc: "Elegant, clean, uncluttered" },
  { value: "round_badge", label: "Round Badge", desc: "Circular glossy premium badge" },
];

export function AIButtonImageDialog({ open, onClose, onApprove, initialPrompt = "" }) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [price, setPrice] = useState("");
  const [style, setStyle] = useState("glossy_pos");
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [preview, setPreview] = useState("");

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error("Describe the image you want"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/button-images/generate`, { prompt: prompt.trim(), price: price.trim(), style }, { headers: authHeader() });
      setPreview(res.data.image_base64);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Image generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await axios.post(`${API}/button-images/approve`, { image_base64: preview }, { headers: authHeader() });
      onApprove(res.data.image_url);
      toast.success("Button image saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save the image");
    } finally {
      setApproving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ai-image-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-600" /> Create Button Image
          </DialogTitle>
          <p className="text-xs text-zinc-500" data-testid="ai-image-reference-note">The selected style's sample image is attached as the reference for the AI.</p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Describe the image</Label>
            <Input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. a bag of BBQ chips, a bottle of orange soda..."
              className="mt-1 h-9"
              disabled={generating}
              data-testid="ai-image-prompt-input"
            />
          </div>
          <div>
            <Label className="text-sm">Price tag (optional)</Label>
            <Input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="e.g. $2.49"
              className="mt-1 h-9"
              disabled={generating}
              data-testid="ai-image-price-input"
            />
          </div>
          {!preview && (
            <div>
              <Label className="text-sm">Style</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {AI_STYLES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    disabled={generating}
                    onClick={() => setStyle(s.value)}
                    data-testid={`ai-image-style-${s.value}`}
                    className={`text-left p-2 rounded-md border text-xs transition-colors ${
                      style === s.value ? "border-purple-500 bg-purple-50 ring-1 ring-purple-400" : "border-zinc-200 hover:border-purple-300"
                    }`}
                  >
                    <div className="font-semibold text-zinc-800">{s.label}</div>
                    <div className="text-zinc-500">{s.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {preview ? (
            <div className="flex flex-col items-center gap-2">
              <img src={preview} alt="Generated preview" data-testid="ai-image-preview" className="w-32 h-32 object-contain rounded border bg-white" />
              <div className="flex gap-2 w-full">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleApprove} disabled={approving} data-testid="ai-image-approve-btn">
                  {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Use This Image"}
                </Button>
                <Button variant="outline" onClick={handleGenerate} disabled={generating} data-testid="ai-image-regenerate-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Again"}
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPreview("")} data-testid="ai-image-discard-btn">
                Discard &amp; edit prompt
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="ai-image-generate-btn"
            >
              {generating ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating image (up to a minute)...</>) : "Create an Image"}
            </Button>
          )}
          <Button variant="outline" className="w-full" onClick={onClose} data-testid="ai-image-cancel-btn">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
